import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Book,
  Shield,
  Users,
  IndianRupee,
  ChartBar,
  ArrowLeft,
  FileText,
  Landmark,
  HandCoins,
  Calendar,
  AlarmCheck,
  Smartphone,
  Lock,
  Database,
  Lightbulb,
  RefreshCw,
  Layers,
  Clock,
  Award,
  GripHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AnimatedGradientText from "@/components/AnimatedGradientText";

const LearnMore = () => {
  const [language, setLanguage] = useState("english");

  const toggleLanguage = () => {
    setLanguage(language === "english" ? "hindi" : "english");
  };

  const content = {
    english: {
      backToHome: "Back to Home",
      simpleGuide: "Simple Guide to Digital Saving Circles",
      understanding: "Understanding Digital Committee",
      subtitle: "A simple guide to how group savings work in the digital age",
      whatIs: "What is a Digital Committee?",
      whatIsDesc1:
        "Think of a digital committee as a savings club where friends pool money together each month. It's like taking turns borrowing from a shared piggy bank when you need it most.",
      whatIsDesc2:
        "You and your friends put money in a pot every month. When it's your turn, you get the whole pot. The twist? Everyone bids on how much discount they'll take to get the money early. The person willing to take the biggest discount gets the pot that month.",
      whyLove: "Why People Love Digital Committee",
      whyLoveDesc:
        "They combine the discipline of saving with the flexibility of borrowing. You get money when you need it and save when you don’t.",
      howWork: "How Digital Committee Work",
      joinGroup: "Join a Group",
      joinGroupDesc:
        "Sign up and join a savings group of 10-20 people. Everyone agrees to put in the same amount each month.",
      joinGroupNote: "No paperwork or in-person meetings needed!",
      monthlyPayments: "Monthly Payments",
      monthlyPaymentsDesc:
        "Pay your monthly amount (like ₹5,000) directly from your bank account or UPI. The app handles everything.",
      monthlyPaymentsNote: "Automatic reminders so you never miss a payment!",
      onlineBidding: "Online Bidding",
      onlineBiddingDesc:
        "Need money this month? Place a bid saying how much of a discount you’ll take. Highest bidder gets the pot.",
      onlineBiddingNote: "Bid from anywhere using your phone!",
      getReturns: "Get Returns",
      getReturnsDesc:
        "Every month, you get a small return from the winning bid discount. It’s like earning interest on your savings!",
      getReturnsNote: "Track your returns in real-time on the app!",
      whyGoDigital: "Why Go Digital?",
      whyGoDigitalDesc:
        "Digital Committee take the hassle out of traditional savings clubs. No more chasing payments or organizing meetings - everything happens on your phone!",
      safeSecure: "Safe & Secure",
      safeSecureDesc:
        "Bank-level security keeps your money safe. ID verification prevents fraud.",
      mobileFirst: "Mobile-First",
      mobileFirstDesc:
        "Manage everything from your phone. Pay, bid, track savings - no paperwork needed.",
      noHiddenFees: "No Hidden Fees",
      noHiddenFeesDesc:
        "See exactly what you pay and earn. Complete transparency with every transaction.",
      legalCompliant: "Legal & Compliant",
      legalCompliantDesc:
        "Fully follows digital committee laws with proper licenses and government approval.",
      timeSaving: "Time-Saving",
      timeSavingDesc:
        "No physical meetings. Do everything online in minutes, not hours.",
      betterReturns: "Better Returns",
      betterReturnsDesc:
        "Digital efficiency means lower costs and potentially higher returns on your savings.",
      comingSoon: "Coming Soon: Dhansamuh App",
      comingSoonDesc:
        "Our app launching soon will make digital committee as easy as ordering food online. Sign up, join a group, and start saving—all without leaving your couch!",
      techSafety: "The Tech That Keeps Your Money Safe",
      techSafetyDesc:
        "You don't need to understand the technology to use our app, but here's what's working behind the scenes to keep your money safe:",
      simpleAuth: "Simple Authentication",
      simpleAuthDesc:
        "What it means for you: Log in securely using your fingerprint or face ID - no more remembering complex passwords.",
      bankSecurity: "Bank-Level Security",
      bankSecurityDesc:
        "What it means for you: The same technology that protects online banking protects your committee fund payments and data.",
      autoPayments: "Automated Payments",
      autoPaymentsDesc:
        "What it means for you: Set up automatic payments so you never miss a contribution. Money moves directly between bank accounts.",
      idVerify: "ID Verification",
      idVerifyDesc:
        "What it means for you: Everyone in your group has their identity verified, so you know exactly who you’re saving with.",
      ctaTitle: "Ready to save smarter?",
      ctaDesc:
        "Be among the first to try our digital committee app. Join our waitlist today to get early access and special perks!",
      ctaButton: "Join Waitlist Now",
      ctaNote: "No obligation, cancel anytime.",
    },
    hindi: {
      backToHome: "होम पर वापस जाएं",
      simpleGuide: "डिजिटल बचत मंडलियों के लिए सरल गाइड",
      understanding: "डिजिटल कमेटी को समझना",
      subtitle:
        "डिजिटल युग में समूह बचत कैसे काम करती है, इसके लिए एक सरल गाइड",
      whatIs: "डिजिटल कमेटी क्या है?",
      whatIsDesc1:
        "डिजिटल कमेटी को एक बचत क्लब के रूप में सोचें जहां दोस्त हर महीने पैसा एक साथ जमा करते हैं। यह ऐसा है जैसे जरूरत पड़ने पर एक साझा गुल्लक से बारी-बारी से उधार लेना।",
      whatIsDesc2:
        "आप और आपके दोस्त हर महीने एक बर्तन में पैसा डालते हैं। जब आपकी बारी आती है, तो आपको पूरा बर्तन मिलता है। ट्विस्ट यह है कि हर कोई बोली लगाता है कि वे पैसे जल्दी लेने के लिए कितना डिस्काउंट लेंगे। जो सबसे बड़ा डिस्काउंट लेने को तैयार होता है, उसे उस महीने बर्तन मिलता है।",
      whyLove: "लोग डिजिटल कमेटी को क्यों पसंद करते हैं",
      whyLoveDesc:
        "यह बचत के अनुशासन को उधार लेने की लचीलापन के साथ जोड़ता है। आपको जरूरत पड़ने पर पैसा मिलता है और जब जरूरत न हो तो बचत करते हैं।",
      howWork: "डिजिटल कमेटी कैसे काम करती है",
      joinGroup: "समूह में शामिल हों",
      joinGroupDesc:
        "साइन अप करें और 10-20 लोगों के बचत समूह में शामिल हों। सभी हर महीने एक ही राशि डालने के लिए सहमत होते हैं।",
      joinGroupNote: "कोई कागजी कार्रवाई या व्यक्तिगत बैठक की जरूरत नहीं!",
      monthlyPayments: "मासिक भुगतान",
      monthlyPaymentsDesc:
        "अपनी मासिक राशि (जैसे ₹5,000) सीधे अपने बैंक खाते या UPI से भुगतान करें। ऐप सब कुछ संभालता है।",
      monthlyPaymentsNote: "स्वचालित रिमाइंडर ताकि आप कभी भुगतान न छोड़ें!",
      onlineBidding: "ऑनलाइन बोली",
      onlineBiddingDesc:
        "इस महीने पैसे चाहिए? बोली लगाएं कि आप कितना डिस्काउंट लेंगे। सबसे ज्यादा बोली लगाने वाला बर्तन लेता है।",
      onlineBiddingNote: "अपने फोन का उपयोग करके कहीं से भी बोली लगाएं!",
      getReturns: "रिटर्न प्राप्त करें",
      getReturnsDesc:
        "हर महीने, आपको जीतने वाली बोली के डिस्काउंट से थोड़ा रिटर्न मिलता है। यह आपकी बचत पर ब्याज कमाने जैसा है!",
      getReturnsNote: "ऐप पर अपने रिटर्न को रीयल-टाइम में ट्रैक करें!",
      whyGoDigital: "डिजिटल क्यों जाएं?",
      whyGoDigitalDesc:
        "डिजिटल कमेटी पारंपरिक बचत क्लबों की परेशानी को दूर करती है। अब भुगतान का पीछा करने या मीटिंग आयोजित करने की जरूरत नहीं - सब कुछ आपके फोन पर होता है!",
      safeSecure: "सुरक्षित और संरक्षित",
      safeSecureDesc:
        "बैंक-स्तरीय सुरक्षा आपके पैसे को सुरक्षित रखती है। आईडी सत्यापन धोखाधड़ी को रोकता है।",
      mobileFirst: "मोबाइल-प्रथम",
      mobileFirstDesc:
        "अपने फोन से सब कुछ प्रबंधित करें। भुगतान करें, बोली लगाएं, बचत ट्रैक करें - कोई कागजी कार्रवाई की जरूरत नहीं।",
      noHiddenFees: "कोई छिपी हुई फीस नहीं",
      noHiddenFeesDesc:
        "आप जो भुगतान करते हैं और कमाते हैं, उसे ठीक-ठीक देखें। हर लेनदेन के साथ पूर्ण पारदर्शिता।",
      legalCompliant: "कानूनी और अनुपालक",
      legalCompliantDesc:
        "उचित लाइसेंस और सरकारी मंजूरी के साथ डिजिटल कमेटी कानूनों का पूरी तरह पालन करता है।",
      timeSaving: "समय की बचत",
      timeSavingDesc:
        "कोई शारीरिक बैठक नहीं। सब कुछ ऑनलाइन मिनटों में करें, घंटों में नहीं।",
      betterReturns: "बेहतर रिटर्न",
      betterReturnsDesc:
        "डिजिटल दक्षता का मतलब कम लागत और आपकी बचत पर संभावित रूप से अधिक रिटर्न।",
      comingSoon: "जल्द आ रहा है: धनसमूह ऐप",
      comingSoonDesc:
        "हमारा जल्द लॉन्च होने वाला ऐप डिजिटल कमेटी को ऑनलाइन खाना ऑर्डर करने जितना आसान बना देगा। साइन अप करें, समूह में शामिल हों, और अपनी सोफे से बाहर निकले बिना बचत शुरू करें!",
      techSafety: "वह तकनीक जो आपके पैसे को सुरक्षित रखती है",
      techSafetyDesc:
        "हमारे ऐप का उपयोग करने के लिए आपको तकनीक को समझने की जरूरत नहीं है, लेकिन यहाँ पर्दे के पीछे आपके पैसे को सुरक्षित रखने के लिए क्या काम कर रहा है:",
      simpleAuth: "साधारण प्रमाणीकरण",
      simpleAuthDesc:
        "आपके लिए इसका मतलब: अपने फिंगरप्रिंट या फेस आईडी का उपयोग करके सुरक्षित रूप से लॉग इन करें - जटिल पासवर्ड याद रखने की जरूरत नहीं।",
      bankSecurity: "बैंक-स्तरीय सुरक्षा",
      bankSecurityDesc:
        "आपके लिए इसका मतलब: वही तकनीक जो ऑनलाइन बैंकिंग की रक्षा करती है, आपके कमेटी फंड भुगतान और डेटा की रक्षा करती है।",
      autoPayments: "स्वचालित भुगतान",
      autoPaymentsDesc:
        "आपके लिए इसका मतलब: स्वचालित भुगतान सेट करें ताकि आप कभी योगदान न छोड़ें। पैसा सीधे बैंक खातों के बीच चलता है।",
      idVerify: "आईडी सत्यापन",
      idVerifyDesc:
        "आपके लिए इसका मतलब: आपके समूह में हर किसी की पहचान सत्यापित होती है, ताकि आपको पता हो कि आप किसके साथ बचत कर रहे हैं।",
      ctaTitle: "क्या आप स्मार्ट तरीके से बचत करने के लिए तैयार हैं?",
      ctaDesc:
        "हमारे डिजिटल कमेटी ऐप को आजमाने वाले पहले लोगों में शामिल हों। आज ही हमारी प्रतीक्षा सूची में शामिल हों और जल्दी पहुंच और विशेष सुविधाएं प्राप्त करें!",
      ctaButton: "अब प्रतीक्षा सूची में शामिल हों",
      ctaNote: "कोई बाध्यता नहीं, कभी भी रद्द करें।",
    },
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50/50">
      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          onClick={toggleLanguage}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          {language === "english" ? "हिन्दी" : "English"}
        </Button>
      </div>

      {/* Decorative elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -right-20 w-96 h-96 rounded-full bg-blue-200/10 blur-3xl"></div>
        <div className="absolute top-3/4 -left-20 w-80 h-80 rounded-full bg-indigo-300/10 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-full max-w-6xl h-px bg-gradient-to-r from-transparent via-blue-200/50 to-transparent"></div>
      </div>

      {/* Header */}
      <header className="container mx-auto pt-10 px-4">
        <Link
          to="/"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors group"
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 mr-2 transition-colors">
            <ArrowLeft size={16} className="text-blue-600" />
          </span>
          <span className="font-medium">{currentContent.backToHome}</span>
        </Link>
        <div className="text-center max-w-4xl mx-auto my-16">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 text-blue-600 text-sm font-medium mb-6">
            <Book size={14} className="mr-2" />
            {currentContent.simpleGuide}
          </div>
          <AnimatedGradientText
            as="h1"
            className={`text-4xl md:text-6xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 font-playfair ${
              language === 'hindi' ? 'lg:leading-[2] leading-[2.5]' : ''
            }`}
          >
            {currentContent.understanding}
          </AnimatedGradientText>
          <p className="text-foreground/70 text-xl max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 pb-32">
        {/* Introduction */}
        <section className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 mb-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 h-full w-1/3 bg-gradient-to-l from-blue-50 to-transparent opacity-50"></div>

          <div className="relative">
            <h2 className={`text-3xl font-display font-bold mb-6 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent inline-block ${
              language === 'hindi'?'lg:leading-[3] leading-[2.8]' : ''
            }`}  >
              {currentContent.whatIs}
            </h2>
            <p className="text-foreground/80 text-lg mb-4 max-w-3xl">
              {currentContent.whatIsDesc1}
            </p>
            <p className="text-foreground/80 text-lg mb-6 max-w-3xl">
              {currentContent.whatIsDesc2}
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 mt-8">
              <div className="flex items-start">
                <div className="mt-1 mr-4 p-3 bg-white rounded-full shadow-sm">
                  <Lightbulb size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-800 mb-2">
                    {currentContent.whyLove}
                  </h3>
                  <p className="text-blue-700/90 text-lg">
                    {currentContent.whyLoveDesc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Digital Committee Work */}
        <section className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-display font-bold mb-8 text-center">
            {currentContent.howWork}
          </h2>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-100/50 rounded-bl-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mr-4">
                    <Users size={24} className="text-indigo-600" />
                  </div>
                  <h3 className="text-2xl font-display font-bold">
                    {currentContent.joinGroup}
                  </h3>
                </div>
                <p className="text-foreground/80 text-lg">
                  {currentContent.joinGroupDesc}
                </p>
                <div className="mt-4 text-sm text-indigo-600">
                  {currentContent.joinGroupNote}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-100/50 rounded-bl-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mr-4">
                    <IndianRupee size={24} className="text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-display font-bold">
                    {currentContent.monthlyPayments}
                  </h3>
                </div>
                <p className="text-foreground/80 text-lg">
                  {currentContent.monthlyPaymentsDesc}
                </p>
                <div className="mt-4 text-sm text-purple-600">
                  {currentContent.monthlyPaymentsNote}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-24 h-24 bg-amber-100/50 rounded-bl-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mr-4">
                    <GripHorizontal size={24} className="text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-display font-bold">
                    {currentContent.onlineBidding}
                  </h3>
                </div>
                <p className="text-foreground/80 text-lg">
                  {currentContent.onlineBiddingDesc}
                </p>
                <div className="mt-4 text-sm text-amber-600">
                  {currentContent.onlineBiddingNote}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 relative overflow-hidden hover:shadow-xl transition-shadow">
              <div className="absolute top-0 right-0 w-24 h-24 bg-green-100/50 rounded-bl-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mr-4">
                    <ChartBar size={24} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-display font-bold">
                    {currentContent.getReturns}
                  </h3>
                </div>
                <p className="text-foreground/80 text-lg">
                  {currentContent.getReturnsDesc}
                </p>
                <div className="mt-4 text-sm text-green-600">
                  {currentContent.getReturnsNote}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Digital Committee */}
        <section className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 mb-16 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-blue-100/50 to-purple-100/50 rounded-full blur-2xl"></div>

          <div className="relative">
            <h2 className="text-3xl font-display font-bold mb-6 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent inline-block">
              {currentContent.whyGoDigital}
            </h2>
            <p className="text-foreground/80 text-lg mb-8 max-w-3xl">
              {currentContent.whyGoDigitalDesc}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="border border-blue-100 rounded-xl overflow-hidden bg-gradient-to-br from-white to-blue-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                    <Shield size={24} className="text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {currentContent.safeSecure}
                  </h3>
                  <p className="text-foreground/80">
                    {currentContent.safeSecureDesc}
                  </p>
                </div>
              </div>

              <div className="border border-indigo-100 rounded-xl overflow-hidden bg-gradient-to-br from-white to-indigo-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-2 bg-gradient-to-r from-indigo-500 to-indigo-600"></div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                    <Smartphone size={24} className="text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {currentContent.mobileFirst}
                  </h3>
                  <p className="text-foreground/80">
                    {currentContent.mobileFirstDesc}
                  </p>
                </div>
              </div>

              <div className="border border-purple-100 rounded-xl overflow-hidden bg-gradient-to-br from-white to-purple-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-purple-600"></div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                    <Database size={24} className="text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {currentContent.noHiddenFees}
                  </h3>
                  <p className="text-foreground/80">
                    {currentContent.noHiddenFeesDesc}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="border border-teal-100 rounded-xl overflow-hidden bg-gradient-to-br from-white to-teal-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-2 bg-gradient-to-r from-teal-500 to-teal-600"></div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4">
                    <Lock size={24} className="text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {currentContent.legalCompliant}
                  </h3>
                  <p className="text-foreground/80">
                    {currentContent.legalCompliantDesc}
                  </p>
                </div>
              </div>

              <div className="border border-amber-100 rounded-xl overflow-hidden bg-gradient-to-br from-white to-amber-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-2 bg-gradient-to-r from-amber-500 to-amber-600"></div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                    <Clock size={24} className="text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {currentContent.timeSaving}
                  </h3>
                  <p className="text-foreground/80">
                    {currentContent.timeSavingDesc}
                  </p>
                </div>
              </div>

              <div className="border border-pink-100 rounded-xl overflow-hidden bg-gradient-to-br from-white to-pink-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-2 bg-gradient-to-r from-pink-500 to-pink-600"></div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                    <Award size={24} className="text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {currentContent.betterReturns}
                  </h3>
                  <p className="text-foreground/80">
                    {currentContent.betterReturnsDesc}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 p-6 rounded-xl bg-gradient-to-r from-blue-100 to-blue-50 shadow-lg border border-blue-200 transform hover:scale-[1.02] transition-all duration-300" style={{
            background: 'linear-gradient(145deg, #ffffff, #e6f0ff)',
            boxShadow: '8px 8px 16px #d1d9e6, -8px -8px 16px #ffffff'
          }}>
            <div className="flex items-start">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-blue-600 shadow-md flex items-center justify-center mr-4 transform hover:rotate-12 transition-transform duration-300">
                <Calendar size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-800 mb-2">
                  {currentContent.comingSoon}
                </h3>
                <p className="text-blue-700/90 text-lg">
                  {currentContent.comingSoonDesc}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Behind */}
        <section className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 mb-16 relative overflow-hidden">
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-gradient-to-br from-indigo-100/30 to-blue-100/30 rounded-full blur-3xl"></div>

          <div className="relative">
            <h2 className="text-3xl font-display font-bold mb-6 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent inline-block">
              {currentContent.techSafety}
            </h2>
            <p className="text-foreground/80 text-lg mb-6 max-w-3xl">
              {currentContent.techSafetyDesc}
            </p>

            <div className="grid gap-6 md:grid-cols-2 mt-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center mr-3">
                    <Layers size={20} className="text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold">
                    {currentContent.simpleAuth}
                  </h3>
                </div>
                <p className="text-foreground/80">
                  {currentContent.simpleAuthDesc}
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center mr-3">
                    <Shield size={20} className="text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold">
                    {currentContent.bankSecurity}
                  </h3>
                </div>
                <p className="text-foreground/80">
                  {currentContent.bankSecurityDesc}
                </p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 mt-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center mr-3">
                    <RefreshCw size={20} className="text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold">
                    {currentContent.autoPayments}
                  </h3>
                </div>
                <p className="text-foreground/80">
                  {currentContent.autoPaymentsDesc}
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 p-6 rounded-xl border border-pink-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center mr-3">
                    <FileText size={20} className="text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold">
                    {currentContent.idVerify}
                  </h3>
                </div>
                <p className="text-foreground/80">
                  {currentContent.idVerifyDesc}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-xl p-10 text-white relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden opacity-20">
              <div className="absolute top-0 left-0 w-full h-20 bg-white/10 -skew-y-3 transform origin-top-left"></div>
              <div className="absolute bottom-0 right-0 w-full h-20 bg-white/10 -skew-y-3 transform origin-bottom-right"></div>
              <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-60 h-60 rounded-full bg-white/10 blur-xl"></div>
            </div>

            <div className="text-center relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {currentContent.ctaTitle}
              </h2>
              <p className="text-white/90 mb-10 max-w-2xl mx-auto text-lg">
                {currentContent.ctaDesc}
              </p>
              <Link to="/waitlist">
                <Button
                  size="lg"
                  className="bg-white hover:bg-blue-50 text-blue-600 hover:text-blue-700 px-8 py-6 text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  {currentContent.ctaButton}
                </Button>
              </Link>
              <p className="text-white/70 mt-6 text-sm">
                {currentContent.ctaNote}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LearnMore;
